import { Dropdown, Menu, Modal, notification } from 'antd';
import { Fragment, PureComponent } from 'react';
import { WithBEPatient, fetchPtDir } from '../../store/actions/app';
import { FAMILY_MEMBERS_ID_TO_NAME, FAMILY_MEMBERS_NAME } from '../../constants';
import { getSearchResults, indexAllPatients } from '../../pages/allPatientsIndex';
import AntAutoCompleteV2 from '../prescriptionPad/rightPanel/workspace/padV2/AntAutoComplete';
import { NewPatient, Patient } from '../../store/types/patient';
import { closeIcon } from '../common/Icons';
import MergePtListItem from '../common/MergePtListItem';
import { getPatientAndRelationPatients } from './utils';
import { track } from '../../tracking';
import { connect } from 'react-redux';
import { GlobalState } from '../../store/reducers';
import { getPatient } from '../../store/selectors/prescriptionPad';
import { Actions } from '../../store/actions';
import { ThunkDispatch } from 'redux-thunk';
import { PatientWithLocalId } from '../../store/types/prescription';
import { updatePatient } from '../../store/actions/prescriptionPad';
import AddNewPatient from './AddNewPatient';
import { AddPtModal } from '../common/newaddpt/NewPatientModal';
import {
    getMySingleDoctorId,
    getSelectedClinicId,
    getVisitByIds,
    upsertPatients,
} from '../../store/idbHandler';
import DoctorSelectorPopup from '../common/DoctorSelectorPopOver';
import { BookAptModalState } from '../../pages/AllPatients';
import SlotModal from '../appointment/slot-modal';
import { Appointment } from '../../api/fetchAppointmentById';
import moment from 'moment';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { GO_TO_APPOINTMENTS } from '../../routes/paths';
import { AppointmentsResponse, fetchTodayQueue } from '../../api/fetchAppointmentsV2';
import { isCNRE, onAppointmentAction } from '../../pages/AppointmentUtils';
import { APWithOptionalpatient } from '../../pages/Appointments';
import bookAppointment from '../../api/bookAppointment';
import { MyDoc } from '../../api/fetchMyProfile';
import { updateAppointment } from '../../firestore/appointments';
import { Timestamp } from 'firebase/firestore';
import { PtListItemViewOnly, PtListItemViewOnlyContent } from './PtListItem';
import { Gender } from '../../pages/NewPatient';
import upsertPatient from '../../api/upsertPatient';

interface Props extends RouteComponentProps {
    pid: string;
    closeMe: () => void;
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    patient: PatientWithLocalId | undefined;
    from: string;
    openModalInEditMode?: boolean;
}

interface State {
    patient?: Patient;
    relationPatients: Patient[];
    isSaving?: boolean;
    searchInput?: string;
    addPtModal?: boolean;
    isRelationsSaved?: boolean;
    bookAptModal: BookAptModalState;
    isStateEditedAfterSave?: boolean;
    todayAppointments?: APWithOptionalpatient[];
}

export interface InitialNewPatientState {
    name?: string;
    phCode?: string;
    phoneNumber?: string;
    gender?: Gender;
    dob?: string;
}

const ADD_NEW_PATIENT_OPTION_ID = 'add-new-patient-option-id';

class AddRelationsModal extends PureComponent<Props, State> {
    state: State = {
        relationPatients: [],
        bookAptModal: {
            show: false,
        },
    };

    componentDidMount = () => {
        const { from } = this.props;
        const { getPatientDetails } = this;
        getPatientDetails();
        track('ADD_RELATIONS_MODAL_MOUNT', { from });
    };

    getPatientDetails = async () => {
        const { pid, openModalInEditMode } = this.props;
        const { fetchTodayAppts } = this;
        const patientAndRelationPatients = await getPatientAndRelationPatients(pid);
        if (!patientAndRelationPatients) {
            track('ERROR', { on: 'getPatientAndRelationPatients' });
            return;
        }
        const { patient, relationPatients } = patientAndRelationPatients;
        const newState = {
            patient,
            relationPatients,
            ...(relationPatients.length > 0 && !openModalInEditMode && { isRelationsSaved: true }),
        };
        this.setState(newState);
        if (relationPatients.length > 0) {
            setTimeout(fetchTodayAppts, 300);
        }
    };

    onSearch = async (value: string): Promise<NewPatient[]> => {
        const { relationPatients, patient } = this.state;
        if (!patient) return [];
        const { patientsFuse } = await indexAllPatients();
        const results = getSearchResults(value, patientsFuse).filteredPatients;
        return new Promise((resolve) => {
            const filteredResults = results.filter((t) => {
                const exists = relationPatients.some((s) => s.id === t.id || t.id === patient.id);
                return !exists && t.id !== patient.id;
            });
            filteredResults.push({ id: ADD_NEW_PATIENT_OPTION_ID } as NewPatient);
            if (value !== ADD_NEW_PATIENT_OPTION_ID) {
                this.setState({ searchInput: value });
            }
            resolve(filteredResults as NewPatient[]);
        });
    };

    onSelect = (data?: NewPatient) => {
        const { relationPatients, patient } = this.state;
        if (!data || !patient || data.id === ADD_NEW_PATIENT_OPTION_ID) return;
        const exists = relationPatients.some((s) => s.id === data.id);
        if (exists || data.id === patient.id) return;
        const newRelation = { pid: data.id, rid: '' };
        const updatedPatient = {
            ...patient,
            relations: [...(patient.relations || []), newRelation],
        };
        if (data.relations) {
            const alreadyRelated = data.relations.some((i) => i.pid === patient.id);
            if (!alreadyRelated) {
                data.relations.push({ pid: patient.localId, rid: '' });
            }
        }
        this.setState({
            relationPatients: [...relationPatients, data],
            patient: updatedPatient,
        });
    };

    onRelationValueChange = (id: string, to: string, value: string) => {
        const { patient, relationPatients } = this.state;
        if (!patient?.id || !patient.relations) return;
        if (to === patient.id) {
            const newRelations = patient.relations.map((relation) =>
                relation.pid === id ? { ...relation, rid: value } : relation,
            );
            this.setState({ patient: { ...patient, relations: newRelations } });
            return;
        }
        const relationIndex = relationPatients.findIndex((i) => i.id === to);
        if (relationIndex === -1) return;
        const updatedRelationPatients = relationPatients.map((relationPatient, index) => {
            if (index !== relationIndex) return relationPatient;
            const newRelations = relationPatient.relations
                ? relationPatient.relations.map((relation) =>
                      relation.pid === patient.id ? { ...relation, rid: value } : relation,
                  )
                : [];
            const relationExits = newRelations.some((relation) => relation.pid === patient.id);
            if (!relationExits && patient.id) {
                newRelations.push({ pid: patient.id, rid: value });
            }
            return { ...relationPatient, relations: newRelations };
        });
        this.setState({ relationPatients: updatedRelationPatients });
    };

    onSuccess = () => {
        const { closeMe } = this.props;
        const { isStateEditedAfterSave, relationPatients } = this.state;
        const { fetchTodayAppts } = this;
        this.setState({ isSaving: false, isRelationsSaved: true });
        if (isStateEditedAfterSave || relationPatients.length === 0) {
            closeMe();
        } else {
            setTimeout(fetchTodayAppts, 300);
        }
    };

    onFailure = () => {
        this.setState({ isSaving: false });
        notification.error({
            message: 'Error in updating patients',
            duration: 5,
        });
    };

    onSave = async () => {
        const { patient: rxPatient, dispatch } = this.props;
        const { patient, relationPatients } = this.state;
        const { onSuccess, onFailure } = this;
        if (!patient || !relationPatients) return;
        this.setState({ isSaving: true });
        const allPatients = [patient, ...relationPatients];
        const updatePromises = allPatients.map((pt) => [upsertPatient(pt)]);
        try {
            await Promise.all([updatePromises, upsertPatients(allPatients)]);
        } catch (error) {
            console.error('Error in updating patients', error);
            track('ERROR', { on: 'updatePatients', error: (error as Error).message });
            onFailure();
            return;
        }
        fetchPtDir(onSuccess, onFailure);
        if (rxPatient) {
            dispatch(updatePatient({ ...rxPatient, relations: patient.relations }));
        }
        track('ADD_RELATIONS_MODAL_SAVE', {
            pid: patient.id,
            relations: relationPatients.length.toString(),
        });
    };

    onDelete = async (id?: string) => {
        const { patient, relationPatients } = this.state;
        if (!id || !patient) return;
        patient.relations = patient.relations?.filter((i) => i.pid !== id);
        this.setState({
            patient,
            relationPatients: relationPatients.filter((i) => i.id !== id),
        });
        const pt = relationPatients.find((i) => i.localId === id);
        if (pt) {
            try {
                const updatedPatient = {
                    ...pt,
                    relations: pt.relations?.filter((i) => i.pid !== patient.localId) || [],
                };
                await upsertPatient(updatedPatient);
            } catch (error) {
                console.error('Error deleting patient relation', error);
                track('ERROR', { on: 'deletePatientRelation', error: (error as Error).message });
            }
        }
    };

    toggleAddPtModal = () => {
        const { addPtModal } = this.state;
        this.setState({
            addPtModal: !addPtModal,
        });
        track('TOGGLE_ADD_NEW_PT_MODAL', {
            action: addPtModal ? 'CLOSE' : 'OPEN',
        });
    };

    onMultiDocBookApt = (docid: string, patient: Patient): void => {
        const clinicId = getSelectedClinicId() || '';
        WithBEPatient<void>(patient, async (bePt) => {
            this.setState({
                bookAptModal: {
                    show: true,
                    docId: docid,
                    ptId: bePt.id,
                    clinicId: clinicId,
                },
            });
            return;
        })(this.props.dispatch);
    };

    onCloseBookAptModal = (): void => {
        this.setState({ bookAptModal: { show: false } });
    };

    handleAppointmentBooking = (apt: Appointment): void => {
        const { fetchTodayAppts } = this;
        const isSameDay = moment(apt.full_date).isSame(moment(), 'day');
        const clinicId = getSelectedClinicId() || '';
        this.props.history.replace(
            GO_TO_APPOINTMENTS(
                clinicId,
                isSameDay ? 'today-queue' : 'upcoming',
                undefined,
                `date=${moment(apt.full_date).format('YYYY-MM-DD')}&left=booked`,
            ),
        );
        setTimeout(fetchTodayAppts, 300);
    };

    getInitialState = (): InitialNewPatientState | undefined => {
        const { searchInput } = this.state;
        if (searchInput && searchInput.trim().length > 0) {
            const trimmedInput = searchInput.trim();
            if (/^\d+$/.test(trimmedInput)) {
                return {
                    phCode: '+91',
                    phoneNumber: trimmedInput,
                };
            } else {
                return {
                    name: trimmedInput,
                };
            }
        }
        return undefined;
    };

    fetchTodayAppts = (): void => {
        const selectedClinicId = getSelectedClinicId();
        if (!selectedClinicId) {
            track('ERROR', {
                on: 'PatientDetails#fetchTodayAppts',
                what: 'No clinic selected',
            });
            return;
        }
        const { relationPatients } = this.state;
        const patientIdsSet = new Set(relationPatients.map((i) => i.localId));
        fetchTodayQueue(selectedClinicId)
            .then(async (response: AppointmentsResponse) => {
                if (!response.response?.apt) {
                    return;
                }
                const list = response.response.apt
                    .filter((t) => t.patient_oid && patientIdsSet.has(t.patient_oid))
                    .filter((t) => !isCNRE(t.status));
                const visitIds = list.map((a) => a.aid);
                const localVisits = (await getVisitByIds(visitIds)) || [];
                const localContextList = list.map((a, index) => ({
                    ...a,
                    isLocalPresnt: localVisits[index] !== undefined,
                    localRxId: localVisits[index]?.prescriptionId,
                }));
                this.setState({
                    todayAppointments: localContextList,
                });
            })
            .catch((e) => console.log(e));
    };

    onAppointmentAction = async (apt: APWithOptionalpatient): Promise<void> => {
        const { closeMe } = this.props;
        onAppointmentAction(this, apt, apt?.clinic_id);
        track('SCHEDULE_APPOINTMENT_ACTION', { status: apt.status, stage: apt.stage });
        closeMe();
    };

    onCheckIn = async (pt: Patient, apt?: APWithOptionalpatient, doc?: MyDoc) => {
        const { fetchTodayAppts } = this;
        const clinicId = getSelectedClinicId() || '';
        if (apt) {
            await updateAppointment({
                data: {
                    status: 'CK',
                    visit_checkin: Timestamp.fromDate(new Date()),
                    full_date: Timestamp.fromDate(new Date()),
                    date: Timestamp.fromDate(new Date()),
                },
                aid: apt.aid,
            });
        } else {
            await bookAppointment({
                docId: doc?.id || getMySingleDoctorId() || '',
                channel: 'Walkin',
                ptOid: pt.localId || '',
                cId: clinicId,
                dateTime: new Date().toISOString(),
            });
        }
        setTimeout(fetchTodayAppts, 300);
    };

    toggleIsRelationsSaved = () => {
        this.setState({ isRelationsSaved: false });
    };

    render() {
        const { closeMe } = this.props;
        const {
            patient,
            relationPatients,
            isSaving,
            searchInput,
            addPtModal,
            isRelationsSaved,
            isStateEditedAfterSave,
            todayAppointments,
        } = this.state;
        const {
            onSearch,
            onSelect,
            onRelationValueChange,
            onSave,
            onDelete,
            toggleAddPtModal,
            getInitialState,
            onCheckIn,
            toggleIsRelationsSaved,
        } = this;
        const docId = getMySingleDoctorId() || '';
        const clinicId = getSelectedClinicId() || '';

        return (
            <Modal
                onCancel={closeMe}
                width={840}
                closable={false}
                visible={true}
                destroyOnClose
                footer={null}
                bodyStyle={{
                    padding: '0px',
                    height: '550px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
                className="overflow-hidden rounded-8"
                style={{ height: '550px' }}
            >
                <div className="w-full h-full text-13" onClick={(e) => e.stopPropagation()}>
                    <div className="h-full flex flex-col rounded-8 overflow-hidden">
                        <div
                            className={`px-16 py-12 flex items-center justify-between border-b border-darwin-neutral_new-200 ${
                                isRelationsSaved ? 'bg-darwin-green-primary text-white' : 'bg-white'
                            }`}
                        >
                            <p className="text-20">
                                {isRelationsSaved
                                    ? 'Relation added successfully'
                                    : 'Add or remove related profiles'}
                            </p>
                            <button className="p-6" onClick={closeMe}>
                                {closeIcon(
                                    isRelationsSaved
                                        ? 'w-12 h-12 fill-white'
                                        : 'w-12 h-12 fill-darwin-neutral-1000',
                                )}
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-16 space-y-16">
                            {patient && (
                                <div className="flex items-center justify-between px-12 py-10 bg-darwin-neutrals-50 rounded-8">
                                    <PtListItemViewOnly pt={patient} />
                                    <PtListItemViewOnlyContent pt={patient} className="pl-8" />
                                </div>
                            )}

                            <div>
                                <p className="text-13 font-700 text-darwin-neutral-1000">
                                    Related profiles
                                </p>
                                <div className="mt-10 mb-15">
                                    {!isRelationsSaved && (
                                        <AntAutoCompleteV2
                                            autoFocus={relationPatients.length === 0}
                                            onSearch={onSearch}
                                            onSelect={onSelect}
                                            getLabelToCompare={(option) => option.id}
                                            placeholder="Search Patient by UHID / Phone / Name"
                                            optionsRenderer={(option) =>
                                                option.id === ADD_NEW_PATIENT_OPTION_ID ? (
                                                    <AddNewPatient
                                                        searchInput={searchInput}
                                                        toggleAddPtModal={toggleAddPtModal}
                                                    />
                                                ) : (
                                                    <MergePtListItem info={option} />
                                                )
                                            }
                                            clearAfterSelect={true}
                                        />
                                    )}
                                </div>
                                <div className="space-y-14">
                                    {patient &&
                                        relationPatients &&
                                        relationPatients?.map((pt) => {
                                            const appts = todayAppointments
                                                ?.filter((i) => i.patient_oid === pt.localId)
                                                .sort((a, b) =>
                                                    new Date(a.created_at) < new Date(b.created_at)
                                                        ? -1
                                                        : 1,
                                                );
                                            const apt = appts?.[0];
                                            return (
                                                <div>
                                                    <div className="flex space-x-10">
                                                        <div className="flex-1 flex space-x-12 items-center justify-between px-12 py-8 bg-darwin-neutrals-50 rounded-8">
                                                            <div className="space-y-4 min-w-200">
                                                                <PtListItemViewOnly pt={pt} />
                                                                <PtListItemViewOnlyContent
                                                                    pt={pt}
                                                                    className="pl-28"
                                                                />
                                                            </div>
                                                            {isRelationsSaved && (
                                                                <div className="flex items-center space-x-15">
                                                                    <div>
                                                                        is the patient's{' '}
                                                                        <b>
                                                                            {FAMILY_MEMBERS_ID_TO_NAME[
                                                                                patient.relations?.find(
                                                                                    (i) =>
                                                                                        i.pid ===
                                                                                        pt.id,
                                                                                )?.rid || ''
                                                                            ] || '(-)'}
                                                                            ,
                                                                        </b>{' '}
                                                                        patient is related as{' '}
                                                                        <b>
                                                                            {' '}
                                                                            {FAMILY_MEMBERS_ID_TO_NAME[
                                                                                pt.relations?.find(
                                                                                    (i) =>
                                                                                        i.pid ===
                                                                                        patient.id,
                                                                                )?.rid || ''
                                                                            ] || '(-)'}
                                                                            .
                                                                        </b>
                                                                    </div>
                                                                    <div
                                                                        className="flex items-center space-x-8 justify-end min-w-253"
                                                                        style={{ minWidth: 253 }}
                                                                    >
                                                                        {!apt ? (
                                                                            <DoctorSelectorPopup
                                                                                onSelect={(doc) =>
                                                                                    onCheckIn(
                                                                                        pt,
                                                                                        undefined,
                                                                                        doc,
                                                                                    )
                                                                                }
                                                                                clinicId={getSelectedClinicId()}
                                                                            >
                                                                                <button className="flex items-center space-x-8 secondary-btn-v3 btn--small px-12 py-6">
                                                                                    <p className="text-12 font-700">
                                                                                        Check In
                                                                                    </p>
                                                                                    <p>
                                                                                        <svg
                                                                                            viewBox="0 0 14 12"
                                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                                            className="w-14 h-12 fill-darwin-accent-bright-blue-800"
                                                                                        >
                                                                                            <path d="M6.00977 9.80664C5.75586 10.0605 5.375 10.0605 5.14648 9.80664C4.89258 9.57812 4.89258 9.19727 5.14648 8.94336L7.35547 6.73438H1.10938C0.753906 6.73438 0.5 6.48047 0.5 6.125C0.5 5.79492 0.753906 5.51562 1.10938 5.51562H7.35547L5.14648 3.30664C4.89258 3.07812 4.89258 2.69727 5.14648 2.44336C5.375 2.21484 5.75586 2.21484 6.00977 2.44336L9.25977 5.71875C9.48828 5.94727 9.48828 6.32812 9.25977 6.55664L6.00977 9.80664ZM9.23438 1.65625C8.87891 1.65625 8.625 1.40234 8.625 1.04688C8.625 0.716797 8.87891 0.4375 9.23438 0.4375H11.2656C12.4844 0.4375 13.5 1.45312 13.5 2.67188V9.57812C13.5 10.8223 12.4844 11.8125 11.2656 11.8125H9.23438C8.87891 11.8125 8.625 11.5586 8.625 11.2031C8.625 10.873 8.87891 10.5938 9.23438 10.5938H11.2656C11.8242 10.5938 12.2812 10.1621 12.2812 9.57812V2.67188C12.2812 2.11328 11.8242 1.65625 11.2656 1.65625H9.23438Z" />
                                                                                        </svg>
                                                                                    </p>
                                                                                </button>
                                                                            </DoctorSelectorPopup>
                                                                        ) : apt?.status === 'BK' ? (
                                                                            <button
                                                                                className="flex items-center space-x-8 secondary-btn-v3 btn--small px-12 py-6"
                                                                                onClick={() =>
                                                                                    onCheckIn(
                                                                                        pt,
                                                                                        apt,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <p className="text-12 font-700">
                                                                                    Check In
                                                                                </p>
                                                                                <p>
                                                                                    <svg
                                                                                        viewBox="0 0 14 12"
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        className="w-14 h-12 fill-darwin-accent-bright-blue-800"
                                                                                    >
                                                                                        <path d="M6.00977 9.80664C5.75586 10.0605 5.375 10.0605 5.14648 9.80664C4.89258 9.57812 4.89258 9.19727 5.14648 8.94336L7.35547 6.73438H1.10938C0.753906 6.73438 0.5 6.48047 0.5 6.125C0.5 5.79492 0.753906 5.51562 1.10938 5.51562H7.35547L5.14648 3.30664C4.89258 3.07812 4.89258 2.69727 5.14648 2.44336C5.375 2.21484 5.75586 2.21484 6.00977 2.44336L9.25977 5.71875C9.48828 5.94727 9.48828 6.32812 9.25977 6.55664L6.00977 9.80664ZM9.23438 1.65625C8.87891 1.65625 8.625 1.40234 8.625 1.04688C8.625 0.716797 8.87891 0.4375 9.23438 0.4375H11.2656C12.4844 0.4375 13.5 1.45312 13.5 2.67188V9.57812C13.5 10.8223 12.4844 11.8125 11.2656 11.8125H9.23438C8.87891 11.8125 8.625 11.5586 8.625 11.2031C8.625 10.873 8.87891 10.5938 9.23438 10.5938H11.2656C11.8242 10.5938 12.2812 10.1621 12.2812 9.57812V2.67188C12.2812 2.11328 11.8242 1.65625 11.2656 1.65625H9.23438Z" />
                                                                                    </svg>
                                                                                </p>
                                                                            </button>
                                                                        ) : apt.status !==
                                                                          'CMNP' ? (
                                                                            <button
                                                                                className="flex items-center space-x-8 secondary-btn-v3 btn--small px-12 py-6"
                                                                                onClick={() =>
                                                                                    this.onAppointmentAction(
                                                                                        apt,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <p className="text-12 font-700">
                                                                                    {apt?.stage ===
                                                                                    'ONGOING'
                                                                                        ? 'Resume Visit'
                                                                                        : 'Start Visit'}
                                                                                </p>
                                                                                <p>
                                                                                    <svg
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        height="1em"
                                                                                        viewBox="0 0 448 512"
                                                                                    >
                                                                                        <path
                                                                                            d="M32 0C14.3 0 0 14.3 0 32V192v96c0 17.7 14.3 32 32 32s32-14.3 32-32V224h50.7l128 128L137.4 457.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L288 397.3 393.4 502.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L333.3 352 438.6 246.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 306.7l-85.8-85.8C251.4 209.1 288 164.8 288 112C288 50.1 237.9 0 176 0H32zM176 160H64V64H176c26.5 0 48 21.5 48 48s-21.5 48-48 48z"
                                                                                            fill="currentColor"
                                                                                        ></path>
                                                                                    </svg>
                                                                                </p>
                                                                            </button>
                                                                        ) : null}
                                                                        <DoctorSelectorPopup
                                                                            onSelect={(myDoc) =>
                                                                                this.onMultiDocBookApt(
                                                                                    myDoc?.id || '',
                                                                                    pt,
                                                                                )
                                                                            }
                                                                            clinicId={clinicId}
                                                                            trigger={'click'}
                                                                        >
                                                                            <button className="flex items-center space-x-8 primary-btn-v3 btn--small px-12 py-6">
                                                                                <p className="text-12 font-700">
                                                                                    Book Slot
                                                                                </p>
                                                                                <p className="relative -top-1">
                                                                                    <svg
                                                                                        viewBox="0 0 12 14"
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        className="w-12 h-14 fill-white"
                                                                                    >
                                                                                        <path d="M4.17188 1.23438V2.25H7.82812V1.23438C7.82812 0.904297 8.08203 0.625 8.4375 0.625C8.76758 0.625 9.04688 0.904297 9.04688 1.23438V2.25H10.0625C10.9512 2.25 11.6875 2.98633 11.6875 3.875V4.28125V5.5V12C11.6875 12.9141 10.9512 13.625 10.0625 13.625H1.9375C1.02344 13.625 0.3125 12.9141 0.3125 12V5.5V4.28125V3.875C0.3125 2.98633 1.02344 2.25 1.9375 2.25H2.95312V1.23438C2.95312 0.904297 3.20703 0.625 3.5625 0.625C3.89258 0.625 4.17188 0.904297 4.17188 1.23438ZM1.53125 5.5V12C1.53125 12.2285 1.70898 12.4062 1.9375 12.4062H10.0625C10.2656 12.4062 10.4688 12.2285 10.4688 12V5.5H1.53125Z" />
                                                                                    </svg>
                                                                                </p>
                                                                            </button>
                                                                        </DoctorSelectorPopup>
                                                                        <Dropdown
                                                                            overlay={
                                                                                <Menu className="rounded-8 cursor-pointer p-0">
                                                                                    {/* <Menu.Item className="p-0 hover:bg-darwin-neutral_new-50">
                                                                                        <div className="flex py-10 px-16 gap-8 items-center">
                                                                                            <span>
                                                                                                <svg
                                                                                                    viewBox="0 0 12 14"
                                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                                    className="w-14 h-12"
                                                                                                >
                                                                                                    <path d="M0.921875 0.625H4.78125C6.33008 0.625 7.625 1.91992 7.625 3.46875C7.625 4.91602 6.55859 6.08398 5.1875 6.28711L7.625 8.72461L10.6465 5.70312C10.875 5.44922 11.2559 5.44922 11.5098 5.70312C11.7383 5.93164 11.7383 6.3125 11.5098 6.54102L8.46289 9.58789L11.4844 12.6094C11.7383 12.8379 11.7383 13.2188 11.4844 13.4473C11.2559 13.7012 10.875 13.7012 10.6465 13.4473L7.59961 10.4258L4.57812 13.4473C4.34961 13.7012 3.96875 13.7012 3.74023 13.4473C3.48633 13.2188 3.48633 12.8379 3.74023 12.584L6.76172 9.5625L3.51172 6.3125H1.53125V8.14062C1.53125 8.49609 1.25195 8.75 0.921875 8.75C0.566406 8.75 0.3125 8.49609 0.3125 8.14062V5.70312V1.23438C0.3125 0.904297 0.566406 0.625 0.921875 0.625ZM4.78125 5.09375C5.66992 5.09375 6.40625 4.38281 6.40625 3.46875C6.40625 2.58008 5.66992 1.84375 4.78125 1.84375H1.53125V5.09375H4.78125Z" />
                                                                                                </svg>
                                                                                            </span>
                                                                                            <p>
                                                                                                Start
                                                                                                Visit
                                                                                            </p>
                                                                                        </div>
                                                                                    </Menu.Item> */}
                                                                                    <Menu.Item
                                                                                        className="p-0 hover:bg-darwin-neutral_new-50"
                                                                                        onClick={() => {
                                                                                            onDelete(
                                                                                                pt.id,
                                                                                            );
                                                                                            this.setState(
                                                                                                {
                                                                                                    isStateEditedAfterSave: true,
                                                                                                },
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <div className="flex py-10 px-16 gap-8 items-center">
                                                                                            <span>
                                                                                                <svg
                                                                                                    viewBox="0 0 12 14"
                                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                                    className="w-12 h-14 fill-red-primary"
                                                                                                >
                                                                                                    <path d="M11.0781 2.15625C11.4082 2.15625 11.6875 2.43555 11.6875 2.76562C11.6875 3.12109 11.4082 3.375 11.0781 3.375H10.7734L10.1641 11.627C10.0879 12.4902 9.40234 13.125 8.53906 13.125H3.43555C2.57227 13.125 1.88672 12.4902 1.81055 11.627L1.20117 3.375H0.921875C0.566406 3.375 0.3125 3.12109 0.3125 2.76562C0.3125 2.43555 0.566406 2.15625 0.921875 2.15625H2.67383L3.61328 0.759766C3.86719 0.378906 4.32422 0.125 4.80664 0.125H7.16797C7.65039 0.125 8.10742 0.378906 8.36133 0.759766L9.30078 2.15625H11.0781ZM4.80664 1.34375C4.73047 1.34375 4.6543 1.39453 4.62891 1.44531L4.14648 2.15625H7.82812L7.3457 1.44531C7.32031 1.39453 7.24414 1.34375 7.16797 1.34375H4.80664ZM9.55469 3.375H2.41992L3.0293 11.5508C3.05469 11.7539 3.23242 11.9062 3.43555 11.9062H8.53906C8.74219 11.9062 8.91992 11.7539 8.94531 11.5508L9.55469 3.375Z" />
                                                                                                </svg>
                                                                                            </span>
                                                                                            <p className="text-red-primary">
                                                                                                Delete
                                                                                                relation
                                                                                            </p>
                                                                                        </div>
                                                                                    </Menu.Item>
                                                                                </Menu>
                                                                            }
                                                                            placement="bottomRight"
                                                                        >
                                                                            <button className="px-4 py-10">
                                                                                <svg
                                                                                    className="w-6 h-12 fill-darwin-neutral_new-800"
                                                                                    viewBox="0 0 4 11"
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                >
                                                                                    <path d="M2 8.46875C2.43164 8.46875 2.8125 8.72266 3.04102 9.07812C3.26953 9.45898 3.26953 9.94141 3.04102 10.2969C2.8125 10.6777 2.43164 10.9062 2 10.9062C1.54297 10.9062 1.16211 10.6777 0.933594 10.2969C0.705078 9.94141 0.705078 9.45898 0.933594 9.07812C1.16211 8.72266 1.54297 8.46875 2 8.46875ZM2 4.40625C2.43164 4.40625 2.8125 4.66016 3.04102 5.01562C3.26953 5.39648 3.26953 5.87891 3.04102 6.23438C2.8125 6.61523 2.43164 6.84375 2 6.84375C1.54297 6.84375 1.16211 6.61523 0.933594 6.23438C0.705078 5.87891 0.705078 5.39648 0.933594 5.01562C1.16211 4.66016 1.54297 4.40625 2 4.40625ZM3.21875 1.5625C3.21875 2.01953 2.96484 2.40039 2.60938 2.62891C2.22852 2.85742 1.74609 2.85742 1.39062 2.62891C1.00977 2.40039 0.78125 2.01953 0.78125 1.5625C0.78125 1.13086 1.00977 0.75 1.39062 0.521484C1.74609 0.292969 2.22852 0.292969 2.60938 0.521484C2.96484 0.75 3.21875 1.13086 3.21875 1.5625Z" />
                                                                                </svg>
                                                                            </button>
                                                                        </Dropdown>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {!isRelationsSaved && (
                                                            <Fragment>
                                                                <p className="flex items-center">
                                                                    is the patient's
                                                                </p>
                                                                <div className="flex space-x-8 space-x-6">
                                                                    <div className="add-pt-dob-select flex items-center">
                                                                        <select
                                                                            required
                                                                            value={
                                                                                patient.relations?.find(
                                                                                    (i) =>
                                                                                        i.pid ===
                                                                                        pt.id,
                                                                                )?.rid
                                                                            }
                                                                            onChange={(e) =>
                                                                                onRelationValueChange(
                                                                                    pt.localId,
                                                                                    patient.localId,
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                            className="w-108 text-neutral-black font-600 focus:outline-none bg-transparent truncate"
                                                                        >
                                                                            <option value="">
                                                                                relation
                                                                            </option>
                                                                            {FAMILY_MEMBERS_NAME.map(
                                                                                (v) => (
                                                                                    <option
                                                                                        key={v.id}
                                                                                        value={v.id}
                                                                                    >
                                                                                        {v.value}
                                                                                    </option>
                                                                                ),
                                                                            )}
                                                                        </select>
                                                                        <div className="mimic-box"></div>
                                                                    </div>
                                                                    <p className="flex items-center">
                                                                        , patient is related as
                                                                    </p>
                                                                    <div className="add-pt-dob-select flex items-center">
                                                                        <select
                                                                            required
                                                                            value={
                                                                                pt.relations?.find(
                                                                                    (i) =>
                                                                                        i.pid ===
                                                                                        patient.id,
                                                                                )?.rid
                                                                            }
                                                                            onChange={(e) =>
                                                                                onRelationValueChange(
                                                                                    patient.localId,
                                                                                    pt.localId,
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                            className="w-108 text-neutral-black font-600 focus:outline-none bg-transparent truncate"
                                                                        >
                                                                            <option value="">
                                                                                related as
                                                                            </option>
                                                                            {FAMILY_MEMBERS_NAME.map(
                                                                                (v) => (
                                                                                    <option
                                                                                        key={v.id}
                                                                                        value={v.id}
                                                                                    >
                                                                                        {v.value}
                                                                                    </option>
                                                                                ),
                                                                            )}
                                                                        </select>
                                                                        <div className="mimic-box"></div>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    className="flex items-center"
                                                                    onClick={() => onDelete(pt.id)}
                                                                >
                                                                    <svg
                                                                        className="w-14 h-16"
                                                                        viewBox="0 0 14 16"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path
                                                                            d="M5.53125 1.5C5.4375 1.5 5.375 1.5625 5.3125 1.625L4.71875 2.5H9.25L8.65625 1.625C8.625 1.5625 8.53125 1.5 8.4375 1.5H5.53125ZM11.0625 2.5H11.4688H13H13.25C13.6562 2.5 14 2.84375 14 3.25C14 3.6875 13.6562 4 13.25 4H12.875L12.125 14.1562C12.0312 15.2188 11.1875 16 10.125 16H3.84375C2.78125 16 1.9375 15.2188 1.84375 14.1562L1.09375 4H0.75C0.3125 4 0 3.6875 0 3.25C0 2.84375 0.3125 2.5 0.75 2.5H1H2.5H2.90625L4.0625 0.78125C4.375 0.3125 4.9375 0 5.53125 0H8.4375C9.03125 0 9.59375 0.3125 9.90625 0.78125L11.0625 2.5ZM11.375 4H2.59375L3.34375 14.0625C3.375 14.3125 3.59375 14.5 3.84375 14.5H10.125C10.375 14.5 10.5938 14.3125 10.625 14.0625L11.375 4Z"
                                                                            fill="#D74B41"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </Fragment>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>

                        <div className="w-full px-16 py-12 flex items-center justify-between border-t border-darwin-neutral_new-200 bg-white">
                            <button
                                className="secondary-btn-v3 btn--small px-16 py-8 disabled:opacity-70 disabled:cursor-not-allowed"
                                onClick={isRelationsSaved ? toggleIsRelationsSaved : closeMe}
                            >
                                {isRelationsSaved ? 'Add another relation' : 'Cancel'}
                            </button>
                            <button
                                className="flex space-x-8 primary-btn-v3 btn--small px-16 py-8 disabled:opacity-70 disabled:cursor-not-allowed"
                                onClick={
                                    isRelationsSaved
                                        ? isStateEditedAfterSave
                                            ? onSave
                                            : closeMe
                                        : onSave
                                }
                                disabled={isSaving}
                            >
                                {isSaving && (
                                    <p>
                                        <svg
                                            className="w-15 h-15 fill-textColor-secondary animate-spin"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                        >
                                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                        </svg>
                                    </p>
                                )}
                                <p>{isRelationsSaved ? 'Done' : 'Save'}</p>
                            </button>
                        </div>
                    </div>
                </div>
                {addPtModal && (
                    <AddPtModal
                        close={toggleAddPtModal}
                        callback={(patient) => onSelect(patient as NewPatient)}
                        hideABHAFlow
                        isAbhaNewFlow={false}
                        docid={docId}
                        cId={clinicId}
                        style={{ zIndex: 1000 }}
                        initialState={getInitialState()}
                    />
                )}
                <SlotModal
                    show={this.state.bookAptModal.show}
                    clinicId={this.state.bookAptModal.clinicId}
                    docId={this.state.bookAptModal.docId}
                    ptId={this.state.bookAptModal.ptId}
                    onClose={this.onCloseBookAptModal}
                    handleAppointmentBooking={this.handleAppointmentBooking}
                />
            </Modal>
        );
    }
}

export default connect((state: GlobalState) => ({
    patient: getPatient(state),
}))(withRouter(AddRelationsModal));

export { AddRelationsModal };
